import { createTheme } from '@mui/material';
import { elementaryLook, LookTokens } from './tokens';
import { colors as elementaryColors } from './tokens/primitives';
import type {} from './types';

type InlineAlertColors = LookTokens['tokens']['components']['alert']['inline'];

const ALERT_SEVERITIES = [
  ['success', 'Success'],
  ['info', 'Info'],
  ['warning', 'Warning'],
  ['error', 'Error'],
] as const;
const ALERT_VARIANTS = ['standard', 'filled', 'outlined'];

// The paints of the built-in alert icons. Custom icons keep their colors.
const ALERT_ICON_PAINTS = [
  elementaryColors.green100,
  elementaryColors.black,
  elementaryColors.orange100,
  elementaryColors.red100,
]
  .map((color) => `[fill="${color}" i]`)
  .join(', ');

// Every variant and severity as an inline alert: text on the surface, with
// the border and icon in the severity's color.
const getInlineAlertOverrides = (inline: InlineAlertColors) =>
  Object.fromEntries(
    ALERT_VARIANTS.flatMap((variant) =>
      ALERT_SEVERITIES.map(([severity, name]) => [
        `${variant}${name}`,
        {
          backgroundColor: inline.background,
          color: inline.text,
          border: `1px solid ${inline[severity]}`,
          '& .MuiAlert-icon': { color: inline[severity] },
          [`& .MuiAlert-icon :is(${ALERT_ICON_PAINTS})`]: {
            fill: inline[severity],
          },
        },
      ]),
    ),
  );

export const createAppTheme = ({
  mode,
  colors,
  tokens,
  shadows,
}: LookTokens) => {
  const {
    content,
    surface,
    border,
    shadow,
    icon,
    status,
    accent,
    chart,
    identity,
    code,
    neutral,
  } = colors;
  const { fontFamily, fontWeight, motion, components } = tokens;
  const {
    typography,
    formControl,
    inputLabel,
    input,
    menu,
    autocomplete,
    dialog,
    drawer,
    divider,
    tooltip,
    alert,
    muiShape,
    spinner,
    link,
    snackbar,
    dropDown,
    iconButton,
  } = components;

  return createTheme({
    palette: {
      mode,
      common: {
        black: content.primary,
        white: content.inverse,
      },
      primary: {
        main: accent.primary,
        dark: accent.primaryStrong,
        contrastText: content.onAccent.primary,
      },
      secondary: {
        main: accent.secondary,
        dark: accent.secondaryStrong,
        contrastText: content.onAccent.secondary,
      },
      warning: {
        main: status.warning.main,
      },
      error: {
        main: status.error.main,
      },
      info: {
        main: status.info.main,
      },
      success: {
        main: status.success.main,
      },
      grey: neutral,
      text: content.translucent,
      divider: border.translucent,
      background: {
        default: surface.base,
        paper: surface.base,
      },
      action: {
        active: icon.translucent.active,
        hover: surface.translucent.hover,
        selected: surface.translucent.selected,
        disabled: icon.translucent.disabled,
        disabledBackground: surface.translucent.disabled,
        focus: surface.translucent.focus,
      },
      content,
      surface,
      border,
      shadow,
      icon,
      status,
      accent,
      chart,
      identity,
      code,
    },

    tokens,

    shape: {
      borderRadius: muiShape.borderRadius,
    },

    typography: {
      fontFamily: fontFamily.sans,
      fontWeightMedium: fontWeight.medium,
      fontWeightBold: fontWeight.semibold,
      h1: {
        fontFamily: fontFamily.brand,
        fontWeight: typography.displayHeadingWeight,
      },
      h2: {
        fontFamily: fontFamily.brand,
        fontWeight: typography.displayHeadingWeight,
      },
      h3: { fontFamily: fontFamily.brand },
      h4: { fontFamily: fontFamily.brand },
      h5: { fontFamily: fontFamily.brand },
    },

    transitions: {
      duration: motion.duration,
      easing: motion.easing,
    },

    components: {
      MuiTypography: {
        defaultProps: {
          color: typography.color,
        },
        styleOverrides: {
          root: {
            fontSize: typography.fontSize,
          },
        },
        variants: [
          {
            props: { variant: 'h3' },
            style: {
              fontWeight: typography.headingWeight,
              fontSize: typography.headingSize.h3,
            },
          },
          {
            props: { variant: 'h4' },
            style: {
              fontWeight: typography.headingWeight,
              fontSize: typography.headingSize.h4,
            },
          },
          {
            props: { variant: 'h5' },
            style: {
              fontWeight: typography.headingWeight,
              fontSize: typography.headingSize.h5,
            },
          },
          {
            props: { variant: 'h6' },
            style: {
              fontWeight: typography.smallHeadingWeight,
              fontSize: typography.headingSize.h6,
            },
          },
          {
            props: { variant: 'body1' },
            style: {
              fontWeight: typography.body1.fontWeight,
              fontSize: typography.body1.fontSize,
              color: typography.body1.color,
            },
          },
          {
            props: { variant: 'caption' },
            style: {
              fontSize: typography.caption.fontSize,
              color: `${typography.caption.color} !important`,
              lineHeight: typography.caption.lineHeight,
            },
          },
        ],
      },

      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },

      MuiFormControl: {
        styleOverrides: {
          root: {
            '.MuiInputLabel-root': {
              paddingRight: formControl.labelPaddingRight,
              paddingTop: formControl.labelPaddingTop,
              color: `${formControl.label} !important`,
              fontSize: formControl.labelFontSize,
            },
            '.MuiInputLabel-shrink': {
              fontSize: 'unset',
              paddingTop: 'unset',
            },
            '.Mui-focused': {
              color: `${formControl.labelFocused} !important`,
            },
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: inputLabel.color,
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            boxShadow: menu.shadow,
            border: `1px solid ${menu.border}`,
            borderRadius: menu.radius,
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: dialog.radius,
            border: `1px solid ${dialog.border}`,
          },
          root: {
            '& .MuiBackdrop-root': {
              backgroundColor: dialog.backdrop,
            },
          },
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: divider.color,
          },
        },
        variants: [
          {
            props: { light: true },
            style: {
              borderColor: divider.lightColor,
            },
          },
        ],
      },

      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            border: `1px solid ${autocomplete.border}`,
            boxShadow: autocomplete.shadow,
            borderRadius: autocomplete.radius,
          },
          popupIndicator: {
            color: autocomplete.popupIndicator.color,
            '&:hover': {
              backgroundColor: autocomplete.popupIndicator.hoverBackground,
            },
            '&.Mui-disabled': {
              color: autocomplete.popupIndicator.disabledColor,
            },
          },
        },
      },

      MuiSelect: {
        styleOverrides: {
          icon: {
            color: dropDown.chevron.color,
            '&.Mui-disabled': { color: dropDown.chevron.disabledColor },
          },
        },
      },

      MuiInputBase: {
        styleOverrides: {
          root: {
            borderRadius: `${input.radius} !important`,
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: input.outlineBorder,
          },
        },
      },

      MuiCircularProgress: {
        styleOverrides: {
          colorPrimary: {
            color: spinner.color,
          },
        },
      },

      MuiLink: {
        defaultProps: {
          underline: link.underlineOnlyOnHover ? 'hover' : 'always',
        },
        styleOverrides: {
          root: link.hoverColor
            ? { '&:hover': { color: link.hoverColor } }
            : {},
        },
      },

      // MUI lightens elevated paper in dark mode with a white overlay, which
      // no look uses.
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },

      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            borderRadius: snackbar.radius,
          },
        },
      },

      MuiTooltip: {
        defaultProps: {
          placement: 'bottom-start',
        },
        styleOverrides: {
          tooltip: {
            background: tooltip.bg,
            color: tooltip.color,
            borderRadius: tooltip.radius,
            padding: tooltip.padding,
            fontSize: tooltip.fontSize,
            fontWeight: tooltip.fontWeight,
            // `width` and not `min-width`: a min-width of fit-content beats
            // max-width in CSS, which is what used to let tooltips grow past
            // the limit.
            width: 'fit-content',
            maxWidth: tooltip.maxWidth,
          },
        },
      },

      ...(iconButton.defaultColor && {
        MuiIconButton: {
          styleOverrides: {
            root: {
              color: iconButton.defaultColor,
              ...(iconButton.disabledColor && {
                '&.Mui-disabled': { color: iconButton.disabledColor },
              }),
            },
          },
        },
      }),

      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: alert.radius,
            alignItems: 'center',
            ...(alert.closeIcon.color && {
              '& .MuiAlert-action .MuiIconButton-root': {
                color: alert.closeIcon.color,
              },
            }),
          },
          ...(alert.inline.background
            ? getInlineAlertOverrides(alert.inline)
            : {
                standardSuccess: {
                  backgroundColor: alert.successBg,
                  color: alert.fg,
                },
                standardError: {
                  backgroundColor: alert.errorBg,
                  color: alert.fg,
                },
                standardWarning: {
                  backgroundColor: alert.warningBg,
                  color: alert.fg,
                },
                filledWarning: {
                  backgroundColor: alert.attentionBg,
                  color: alert.fg,
                },
                standardInfo: {
                  color: alert.fg,
                  backgroundColor: alert.infoBg,
                },
              }),
        },
        defaultProps: { severity: 'info' },
      },
    },

    mixins: {
      drawerFooter: {
        height: drawer.footerHeight,
        minHeight: drawer.footerHeight,
      },
    },
    ...(shadows && { shadows }),
  });
};

const theme = createAppTheme(elementaryLook);

export default theme;
